/**
 * Booking: the firm's rules, its starting meeting types (every price and allowance is edited in
 * Settings; these are only where a firm begins), and the time arithmetic behind open slots.
 * Times are wall-clock in the firm's time zone; instants are UTC milliseconds.
 */
import { z } from "zod";

export type MeetingRule = "none" | "monthly_plan" | "plan_extra" | "tax_season" | "notice" | "urgent";

export const MEETING_RULE_LABELS: Record<MeetingRule, string> = {
  none: "Anyone can book",
  monthly_plan: "Included with an accounting or CFO plan (per month)",
  plan_extra: "Extra meeting for accounting or CFO plan clients",
  tax_season: "Included for tax clients during tax season (per season)",
  notice: "Free while the client has an open IRS or state notice",
  urgent: "Urgent: same or next business day",
};

export type BookingSettings = {
  /** Earliest a meeting can be booked, in hours from now (urgent slots use urgentNoticeHours). */
  minNoticeHours: number;
  urgentNoticeHours: number;
  /** How far ahead the calendar opens. */
  maxDaysAhead: number;
  /** Free time kept between meetings. */
  bufferMinutes: number;
  /** Cancel at least this many hours before: no charge, and an included meeting is given back. */
  cancelHours: number;
  /** Tax season and the extension season, as MM-DD (inclusive). */
  taxSeason: { start: string; end: string };
  extensionSeason: { start: string; end: string } | null;
  /** Each staff member's own video meeting link, used until Google Meet is connected. */
  meetingLinks: Record<string, string>;
  /** Text confirmations, reminders and cancellations to people who opted in (needs Twilio). */
  textReminders: boolean;
};

export const BOOKING_DEFAULTS: BookingSettings = {
  minNoticeHours: 24,
  urgentNoticeHours: 2,
  maxDaysAhead: 30,
  bufferMinutes: 15,
  cancelHours: 24,
  taxSeason: { start: "01-15", end: "04-15" },
  extensionSeason: { start: "04-16", end: "10-15" },
  meetingLinks: {},
  textReminders: false,
};

const mmdd = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "Use MM-DD");
export const bookingSettingsSchema = z
  .object({
    minNoticeHours: z.number().int().min(0).max(336),
    urgentNoticeHours: z.number().int().min(0).max(48),
    maxDaysAhead: z.number().int().min(1).max(180),
    bufferMinutes: z.number().int().min(0).max(120),
    cancelHours: z.number().int().min(0).max(336),
    taxSeason: z.object({ start: mmdd, end: mmdd }).strict(),
    extensionSeason: z.object({ start: mmdd, end: mmdd }).strict().nullable(),
    meetingLinks: z.record(z.string().uuid(), z.string().trim().url().startsWith("https://").max(500)),
    textReminders: z.boolean(),
  })
  .partial()
  .strict();

/** The firm's settings with defaults filled in. */
export function bookingSettings(stored: unknown): BookingSettings {
  const s = (stored && typeof stored === "object" ? stored : {}) as Partial<BookingSettings>;
  return { ...BOOKING_DEFAULTS, ...s, meetingLinks: { ...(s.meetingLinks ?? {}) } };
}

export type MeetingTypeSeed = {
  key: string;
  name: string;
  description: string;
  durationMinutes: number;
  priceCents: number;
  businessPriceCents: number;
  audience: "public" | "clients";
  rule: MeetingRule;
  includedCount: number;
  location: "video" | "phone";
};

/** Where a new firm starts; the owner changes any of it in Settings › Booking. */
export const DEFAULT_MEETING_TYPES: MeetingTypeSeed[] = [
  { key: "discovery", name: "Discovery call", description: "A short call to talk about what you need and how we can help.", durationMinutes: 15, priceCents: 0, businessPriceCents: 0, audience: "public", rule: "none", includedCount: 0, location: "video" },
  { key: "consultation", name: "Consultation", description: "A working session on your taxes or your business.", durationMinutes: 60, priceCents: 15_000, businessPriceCents: 25_000, audience: "public", rule: "none", includedCount: 0, location: "video" },
  { key: "urgent", name: "Urgent consultation", description: "Same or next business day, for something that can't wait.", durationMinutes: 30, priceCents: 15_000, businessPriceCents: 25_000, audience: "public", rule: "urgent", includedCount: 0, location: "video" },
  { key: "tax_season_call", name: "Tax season call", description: "A short call about your return during tax season.", durationMinutes: 20, priceCents: 0, businessPriceCents: 0, audience: "clients", rule: "tax_season", includedCount: 3, location: "phone" },
  { key: "notice_call", name: "Notice call", description: "About an IRS or state notice on a return we prepared.", durationMinutes: 30, priceCents: 0, businessPriceCents: 0, audience: "clients", rule: "notice", includedCount: 0, location: "video" },
  { key: "monthly_review", name: "Monthly review", description: "Your monthly meeting, included with your plan.", durationMinutes: 60, priceCents: 0, businessPriceCents: 0, audience: "clients", rule: "monthly_plan", includedCount: 1, location: "video" },
  { key: "extra_meeting", name: "Additional meeting", description: "Another meeting beyond the one your plan includes.", durationMinutes: 60, priceCents: 25_000, businessPriceCents: 25_000, audience: "clients", rule: "plan_extra", includedCount: 0, location: "video" },
  { key: "tax_planning", name: "Tax planning consultation", description: "Talk through the year ahead (income, business, retirement and family changes) and whether a tax plan would save you money.", durationMinutes: 45, priceCents: 0, businessPriceCents: 0, audience: "public", rule: "none", includedCount: 0, location: "video" },
];

// --- Time ------------------------------------------------------------------------------------

/** The UTC instant of a wall-clock time (minutes after midnight) on a day in a time zone. */
export function zonedInstant(day: string, minute: number, timeZone: string): number {
  const wall = Date.parse(`${day}T00:00:00Z`) + minute * 60_000;
  const offsetAt = (t: number) => {
    const p = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(t));
    const g = (k: string) => Number(p.find((x) => x.type === k)!.value);
    return Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second")) - t;
  };
  // Two passes settle the offset across daylight-saving changes.
  let t = wall - offsetAt(wall);
  t = wall - offsetAt(t);
  return t;
}

/** The day (YYYY-MM-DD), weekday (0 = Sunday) and minute of the day of an instant in a time zone. */
export function zonedParts(t: number, timeZone: string): { day: string; weekday: number; minute: number } {
  const p = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short" }).formatToParts(new Date(t));
  const g = (k: string) => p.find((x) => x.type === k)!.value;
  const day = `${g("year")}-${g("month")}-${g("day")}`;
  return { day, weekday: new Date(`${day}T12:00:00Z`).getUTCDay(), minute: Number(g("hour")) * 60 + Number(g("minute")) };
}

const addDay = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export type HoursWindow = { weekday: number; startMinute: number; endMinute: number };
export type Busy = { start: number; end: number };

/**
 * Start times a person can take a meeting of `duration` minutes: inside their weekly hours, on a
 * half-hour grid (quarter-hour for meetings under 30 minutes), not before `earliest` or after
 * `latest`, and clear of every busy time with `buffer` minutes on each side.
 */
export function openSlots(o: { hours: HoursWindow[]; busy: Busy[]; fromDay: string; toDay: string; duration: number; buffer: number; earliest: number; latest: number; timeZone: string }): number[] {
  const step = o.duration < 30 ? 15 : 30;
  const out: number[] = [];
  for (let day = o.fromDay; day <= o.toDay; day = addDay(day, 1)) {
    const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
    for (const w of o.hours.filter((h) => h.weekday === weekday)) {
      for (let m = Math.ceil(w.startMinute / step) * step; m + o.duration <= w.endMinute; m += step) {
        const start = zonedInstant(day, m, o.timeZone);
        const end = start + o.duration * 60_000;
        if (start < o.earliest || start > o.latest) continue;
        const pad = o.buffer * 60_000;
        if (o.busy.some((b) => start < b.end + pad && end > b.start - pad)) continue;
        out.push(start);
      }
    }
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/** Next business day (Monday–Friday) after a day. */
export function nextWeekday(day: string): string {
  let d = addDay(day, 1);
  while ([0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay())) d = addDay(d, 1);
  return d;
}

/**
 * The tax season a day falls in, if any: the season (or extension season) of that calendar year,
 * whose returns are for the year before. Windows are MM-DD, inclusive.
 */
export function taxSeasonOn(day: string, s: Pick<BookingSettings, "taxSeason" | "extensionSeason">): { seasonYear: number; taxYear: number; window: "season" | "extension" } | null {
  const md = day.slice(5);
  const year = Number(day.slice(0, 4));
  const inside = (w: { start: string; end: string }) => (w.start <= w.end ? md >= w.start && md <= w.end : md >= w.start || md <= w.end);
  if (inside(s.taxSeason)) return { seasonYear: year, taxYear: year - 1, window: "season" };
  if (s.extensionSeason && inside(s.extensionSeason)) return { seasonYear: year, taxYear: year - 1, window: "extension" };
  return null;
}

/** The price a client pays for a meeting type: businesses have their own price. */
export function meetingPrice(t: { priceCents: number; businessPriceCents: number }, kind: "individual" | "business"): number {
  return kind === "business" ? t.businessPriceCents : t.priceCents;
}

/** The opt-in shown next to the texts checkbox, word for word as registered with the carriers. */
export function smsConsentText(firm: string): string {
  return `Text me appointment confirmations, reminders and cancellations from ${firm}`;
}
/** Recorded with each opt-in, so the wording a person agreed to can be shown later. Change it when the wording changes. */
export const SMS_CONSENT_VERSION = "2026-10-18";
export const SMS_DISCLOSURE = "Message frequency varies (about 1–3 messages per appointment). Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of service.";

/** A US mobile number as +1XXXXXXXXXX, or null if it isn't one. */
export function usPhone(raw: string): string | null {
  const digits = raw.replace(/[^0-9]/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return /^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(ten) ? `+1${ten}` : null;
}

/** "(301) 555-0143" for display. */
export function phoneLabel(e164: string): string {
  const m = /^\+1([0-9]{3})([0-9]{3})([0-9]{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** "Tue, Oct 20 at 1:00 PM EDT": short enough for a text. */
export function smsWhen(t: number, timeZone: string): string {
  const day = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(t);
  const time = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(t);
  // Intl puts a narrow no-break space before AM/PM, which would make the text Unicode (half the length per message).
  return `${day} at ${time}`.replace(/[\u00a0\u2009\u202f]/g, " ");
}

/**
 * The texts, as registered with the carriers: no links, the firm's name first, and how to opt out.
 * Plain characters only, so each fits one or two message segments.
 */
export function smsText(kind: "confirmation" | "reminder" | "cancellation", v: { firm: string; meeting: string; at: string; phone: boolean }): string {
  const what = v.meeting.toLowerCase();
  switch (kind) {
    case "confirmation":
      return `${v.firm}: Your ${what} is confirmed for ${v.at}. Details are in your email. Reply STOP to opt out.`;
    case "reminder":
      return `${v.firm} reminder: Your ${what} is ${v.at}. ${v.phone ? "We will call you." : "The video link is in your email."} Reply STOP to opt out, HELP for help.`;
    case "cancellation":
      return `${v.firm}: Your ${what} on ${v.at} is cancelled. To book again, see your email. Reply STOP to opt out.`;
  }
}
